import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { patientDetailQueryKey } from './usePatients';
import type { Json } from '@/types/database.generated';

export type ConsentStatus = 'not_set' | 'pending' | 'accepted' | 'rejected' | 'revoked';
export type ConsentType = 'gdpr' | 'treatment' | 'marketing' | 'data_processing' | 'other';

export interface ConsentRecord {
  status: ConsentStatus;
  type?: ConsentType;
  method?: 'email' | 'verbal' | 'written' | 'portal';
  notes?: string;
  signed_at?: string;
  updated_at: string;
  updated_by?: string;
}

export interface UpdateConsentInput {
  patient_id: string;
  consent: ConsentRecord;
}

export function useUpdatePatientConsent() {
  const qc = useQueryClient();
  
  return useMutation({
    mutationFn: async (input: UpdateConsentInput) => {
      const consentData: Json = {
        status: input.consent.status,
        type: input.consent.type,
        method: input.consent.method,
        notes: input.consent.notes,
        signed_at: input.consent.signed_at ?? new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      const { data, error } = await supabase
        .from('patients')
        .update({ consent: consentData })
        .eq('id', input.patient_id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: patientDetailQueryKey(data.id) });
    },
  });
}
